/**
 * V-sit. Its four thresholds were **measured** off the take of 2026-10-13 —
 * shallow folds, bent knees, clean reps and lying leg raises, five of each in
 * one recording — and all four moved because of it. It still ships
 * `calibrated: false`, and for something that is not a threshold: the counter
 * also counts the athlete settling into position and getting up again, two
 * folds in that take that nothing in this signal tells apart from a rep.
 * See docs/v-sit.md.
 *
 * Lying on the back, the trunk and the legs rise to meet in a V and come back
 * down. So the signal is the hip angle — shoulder-hip-knee, 180 flat, closing as
 * the body folds — counted with the oscillation counter the same way a squat's
 * knee is.
 *
 * **The hip angle is what counts a rep, and it is not enough to judge one.**
 * Shoulder-hip-knee closes exactly as well when the legs come up to a trunk that
 * stayed on the floor, so a lying leg raise scored a clean V-sit until the
 * athlete reported it. The two are kept apart the way the handstand keeps being
 * inverted apart from being in line: the hip says an attempt happened, and a
 * criterion of its own says the trunk came up too. The rep is still counted and
 * cued rather than rejected — a leg raise performed where a V-sit was asked for
 * is a rep done wrong, and the athlete has to be told which thing was wrong.
 *
 * Two things are specific to doing this on the floor with the phone propped up,
 * and both cost a rep if they are got wrong:
 *
 * - **The ankles are the landmarks most often lost.** Lying down they are the
 *   furthest from the camera, they leave the frame first, and on the floor they
 *   are often behind the near leg. So this gates on shoulder, hip and knee —
 *   `foldVisibility` — rather than on the leg chain the squat uses, which starts
 *   at the knee and runs down to the heel.
 * - **Coming back down is half the rep, and it is the counter that enforces it,
 *   not a criterion.** A rep closes only when the hip angle returns above
 *   `vSitHipUp`, which is set at a full extension, so a bounced rep that never
 *   unfolds is not counted at all rather than counted and failed. A "came back
 *   down" criterion was written first and removed: a rep's samples end at the
 *   frame that closed it, so the furthest the hip opened within the rep IS the
 *   threshold, and the criterion could only ever restate it. It passed by
 *   construction or failed by construction depending on where the number was put,
 *   which is worse than not measuring it.
 */
import { OscillationCounter } from "./oscillation";

export const vSit = {
  id: "v_sit",
  name: "V-sit",
  blurb: "Lie flat, fold to a V, come all the way back",
  /**
   * Side-on, like the other two. The fold happens in the sagittal plane, and
   * from the front or from the feet it is along the axis the geometry drops —
   * the z MediaPipe is weakly supervised on, which is the whole reason the
   * metrics are a projection. Camera at the athlete's side, on the floor.
   */
  view: "side",
  calibrated: false,
  thresholdKeys: [
    "vSitHipDown",
    "vSitHipUp",
    "vSitFoldMax",
    "vSitKneeMin",
    "vSitTrunkRise",
  ],
  cues: {
    v_sit_fold: "Fold tighter",
    v_sit_knees: "Straighten your legs",
    v_sit_trunk: "Lift your chest too",
  },
  needsFootScale: false,
  createCounter: (thresholds) =>
    new OscillationCounter({
      signal: (sample, side) => sample.hip[side],
      low: thresholds.vSitHipDown,
      high: thresholds.vSitHipUp,
      visibility: (sample, side) => sample.foldVisibility[side],
      minVisibility: thresholds.minVisibility,
      maxGapMs: thresholds.maxGapMs,
    }),
  scoreRep: (samples, { side, thresholds }) => {
    if (samples.length === 0) {
      return [];
    }
    const seen =
      Math.min(...samples.map((s) => s.foldVisibility[side])) >=
      thresholds.minVisibility;
    const tightest = Math.min(...samples.map((s) => s.hip[side]));
    // The straightest the knee got while the body was folded, not over the whole
    // rep: the legs bend on the way down and that is not the fault being looked
    // for. Taken over the frames at or near the top of the fold.
    //
    // A shallow rep has no such frames, and that is NOT a reason to abstain. A
    // fold that passed `vSitHipDown` but never reached `vSitFoldMax` is a rep
    // the athlete performed badly, and a null knee on it would print "not fully
    // seen" — which tells them to move the phone when what was wrong was the
    // fold. So the window falls back to the frames that made it a rep at all,
    // which is non-empty by construction: the counter only closed a rep because
    // the hip went below `vSitHipDown`. Null here stays reserved for landmarks
    // the model was guessing at.
    const tight = samples.filter((s) => s.hip[side] <= thresholds.vSitFoldMax);
    const folded =
      tight.length > 0
        ? tight
        : samples.filter((s) => s.hip[side] <= thresholds.vSitHipDown);
    const knee =
      seen && folded.length > 0
        ? Math.max(...folded.map((s) => s.knee[side]))
        : null;

    // How far the trunk came off the floor within this rep. `trunkTilt` is
    // signed off world vertical, so lying flat reads near 90 either way and
    // sitting up reads near 0; the magnitude subtracted from 90 is elevation off
    // the floor, 0 flat. Read as a rise from the rep's own flattest frame rather
    // than as an absolute angle, because the floor is not a landmark and the
    // phone is propped on it: a tilted phone shifts both ends equally and the
    // difference survives it.
    const elevation = samples.map((s) => 90 - Math.abs(s.trunkTilt));
    const rise = Math.max(...elevation) - Math.min(...elevation);

    return [
      {
        id: "v_sit_fold",
        label: "Fold",
        value: seen ? tightest : null,
        unit: "°",
        precision: 0,
        pass: seen ? tightest <= thresholds.vSitFoldMax : null,
      },
      {
        id: "v_sit_knees",
        label: "Legs straight",
        value: knee,
        unit: "°",
        precision: 0,
        pass: knee == null ? null : knee >= thresholds.vSitKneeMin,
      },
      {
        id: "v_sit_trunk",
        label: "Chest up",
        value: seen ? rise : null,
        unit: "°",
        precision: 0,
        pass: seen ? rise >= thresholds.vSitTrunkRise : null,
      },
    ];
  },
  metricIds: [],
};
